﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_VIDEO_THEME_QUERY_KEY,
  VIDEO_THEME_API_URL,
  VIDEO_THEME_BY_ID_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options, Theme } from '@/types';

export const videoThemeEndpoints = {
  getAllVideoTheme: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    organizationId?: number,
    type?: number
  ) =>
    axios.get(
      `${apiUrl()}${VIDEO_THEME_API_URL}${getQueryParams({
        'paging.PageSize': pageSize,
        'paging.Cursor': cursor,
        'sorting.Field': 'Order',
        'sorting.Asc': true,
        'search.Text': searchQuery as string,
        'search.Fields': ['name', 'tags'],
        'filter.OrganizationId': organizationId as number,
        'filter.Type': type as number
      })}`
    ),
  getById: (id: number) => axios.get(`${apiUrl()}${VIDEO_THEME_API_URL}/${id}`),
  createTheme: (payload: Theme) => axios.post(`${apiUrl()}${VIDEO_THEME_API_URL}`, payload),
  updateTheme: (payload: Theme) => axios.put(`${apiUrl()}${VIDEO_THEME_API_URL}`, payload),
  deleteTheme: (id: number) => axios.delete(`${apiUrl()}${VIDEO_THEME_API_URL}/${id}`),
  updatePublish: (payload: { id: number; isPublish: boolean }) =>
    axios.patch(`${apiUrl()}${VIDEO_THEME_API_URL}/publish`, payload),
  updatePreviewVideoUrl: (payload: { id: number; previewVideoUrl: string | null }) =>
    axios.patch(`${apiUrl()}${VIDEO_THEME_API_URL}/preview-theme`, payload)
};

export const useGetAllVideoTheme = (
  pageSize: number,
  searchQuery?: string,
  organizationId?: number,
  type?: number
) => {
  return useInfiniteQuery(
    [ALL_VIDEO_THEME_QUERY_KEY, { pageSize, searchQuery, type }],
    ({ pageParam }) =>
      videoThemeEndpoints.getAllVideoTheme(pageSize, pageParam, searchQuery, organizationId, type),
    {
      keepPreviousData: true,
      enabled: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetVideoThemeById = (id: number, options?: Options) => {
  return useQuery([VIDEO_THEME_BY_ID_QUERY_KEY, { id }], () => videoThemeEndpoints.getById(id), {
    enabled: !!id,
    retry: false,
    onSuccess: () => {
      options?.onSuccess();
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useCreateVideoThemeMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeEndpoints.createTheme, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateVideoThemeMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeEndpoints.updateTheme, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_QUERY_KEY]);
      queryClient.invalidateQueries([VIDEO_THEME_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteVideoThemeMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeEndpoints.deleteTheme, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdatePublish = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeEndpoints.updatePublish, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdatePreviewVideoUrl = (queryClient: QueryClient, options: Options) => {
  return useMutation(videoThemeEndpoints.updatePreviewVideoUrl, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_VIDEO_THEME_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
